/**
 * Tab-Specific Column Overrides for Production Line
 * Defines columns that are unique to each tab (workflow checkboxes, dates, status, etc.)
 */

import type { OrderTableColumn } from '../../../types/components';
import type { ProductionLineStage } from '../../../utils/types';

/**
 * Dashboard Tab - Production dates and completion status columns
 */
const DASHBOARD_TAB_COLUMNS: OrderTableColumn[] = [
  // Production dates (only on Dashboard tab, editable for first order per job)
  { 
    key: 'glass_arrival_date', 
    label: 'Glass Arrival Date', 
    type: 'component', 
    group: 'production_dates', 
    width: '140px', 
    align: 'center', 
    condition: { alwaysVisible: true } 
  },
  { 
    key: 'manufacture_date_editable', 
    label: 'Date of Manufacture', 
    type: 'component', 
    group: 'production_dates', 
    width: '140px', 
    align: 'center', 
    condition: { alwaysVisible: true } 
  },
  { 
    key: 'potential_delivery_date', 
    label: 'Potential Delivery Date', 
    type: 'component', 
    group: 'production_dates', 
    width: '160px', 
    align: 'center', 
    condition: { alwaysVisible: true } 
  },
  
  // Manufacturing completion status columns
  { 
    key: 'glass_arrived_completed', 
    label: 'Glass Arrived', 
    type: 'boolean', 
    group: 'production_line', 
    width: '110px', 
    align: 'center', 
    condition: { 
      alwaysVisible: true,
      styles: {
        backgroundColor: (_v: any, order: any) => {
          const isScratched = order.metadata?.glass_arrival_workflow?.scratched ?? false;
          const isArrived = order.metadata?.glass_arrival_workflow?.arrived ?? false;
          
          // Check if ALL glass sides that have values are cutting glass (not arrival)
          const glassValues = [
            { hasValue: !!order.metadata?.glass_side_one_value_id, isArrival: order.glass_side_one_is_arrival },
            { hasValue: !!order.metadata?.glass_side_two_value_id, isArrival: order.glass_side_two_is_arrival },
            { hasValue: !!(order.metadata?.middle_glass_panel_value_id || order.metadata?.glass_side_three_value_id || order.metadata?.woo_glass_raw?.side_three), isArrival: order.middle_glass_panel_is_arrival }
          ];
          
          const sidesWithValues = glassValues.filter(g => g.hasValue);
          const allAreCutting = sidesWithValues.length > 0 && sidesWithValues.every(g => g.isArrival === false);
          
          // Check if order has NO processing features (holes, cutouts, rounded corners)
          const hasNoProcessingFeatures = 
            !order.hole_size_mm && 
            !order.cutout_shape && 
            !order.rounded_corner_radius_mm;
          
          // Gray out only if: all glass is cutting AND no processing features
          const shouldGrayOut = allAreCutting && hasNoProcessingFeatures;
          
          if (isScratched) return 'bg-red-200';
          if (isArrived) return 'bg-green-200';
          if (shouldGrayOut) return 'bg-gray-300';
          return 'bg-white';
        }
      }
    }, 
    render: (value: any, order: any) => {
      // No text for any status - just colors
      return '';
    } 
  },
  { 
    key: 'glass_cutting_completed', 
    label: 'Glass Cut', 
    type: 'boolean', 
    group: 'production_line', 
    width: '90px', 
    align: 'center', 
    condition: { 
      alwaysVisible: true,
      styles: {
        backgroundColor: (_v: any, order: any) => {
          // GREEN: If glass cut checkbox is checked on Glass Cutting tab
          const isCutChecked = order.metadata?.glass_cutting_workflow?.cut_completed ?? false;
          if (isCutChecked) return 'bg-green-200';
          
          // Check if AT LEAST ONE glass side has cutting glass (is_arrival = false)
          const glassValues = [
            { hasValue: !!order.metadata?.glass_side_one_value_id, isArrival: order.glass_side_one_is_arrival },
            { hasValue: !!order.metadata?.glass_side_two_value_id, isArrival: order.glass_side_two_is_arrival },
            { hasValue: !!(order.metadata?.middle_glass_panel_value_id || order.metadata?.glass_side_three_value_id || order.metadata?.woo_glass_raw?.side_three), isArrival: order.middle_glass_panel_is_arrival }
          ];
          
          const sidesWithValues = glassValues.filter(g => g.hasValue);
          const hasAnyCutting = sidesWithValues.some(g => g.isArrival === false);
          
          // Check if order has processing features (holes, cutouts, rounded corners)
          const hasProcessingFeatures = 
            !!order.hole_size_mm || 
            !!order.cutout_shape || 
            !!order.rounded_corner_radius_mm;
          
          // GRAY: If NO cutting glass OR has processing features
          const shouldGrayOut = !hasAnyCutting || hasProcessingFeatures;
          if (shouldGrayOut) return 'bg-gray-300';
          
          // WHITE: has cutting glass AND no processing features (or default)
          return 'bg-white';
        }
      }
    }, 
    render: (value: any) => '' 
  },
  { 
    key: 'sealing_completed', 
    label: 'Sealing', 
    type: 'boolean', 
    group: 'production_line', 
    width: '80px', 
    align: 'center', 
    condition: { 
      alwaysVisible: true,
      styles: {
        backgroundColor: (_v: any, order: any) => {
          // GREEN: If sealed checkbox is checked on Sealing & Allocation tab
          const isSealed = order.metadata?.sealing_workflow?.sealed ?? false;
          if (isSealed) return 'bg-green-200';
          
          // GRAY: If product type is single or mirror
          const isSingleOrMirror = order.product_type === 'single' || order.product_type === 'mirror';
          if (isSingleOrMirror) return 'bg-gray-300';
          
          // WHITE: Default
          return 'bg-white';
        }
      }
    }, 
    render: (value: any) => '' 
  },
  { 
    key: 'spacer_cutting_completed', 
    label: 'Spacer Cut', 
    type: 'boolean', 
    group: 'production_line', 
    width: '90px', 
    align: 'center', 
    condition: { 
      alwaysVisible: true,
      styles: {
        backgroundColor: (_v: any, order: any) => {
          // GREEN: If spacer done checkbox is checked on Spacer Bar Cutting tab
          const isSpacerDone = order.metadata?.spacer_workflow?.done ?? false;
          if (isSpacerDone) return 'bg-green-200';
          
          // GRAY: If product type is single or mirror
          const isSingleOrMirror = order.product_type === 'single' || order.product_type === 'mirror';
          if (isSingleOrMirror) return 'bg-gray-300';
          
          // WHITE: Default
          return 'bg-white';
        }
      }
    }, 
    render: (value: any) => '' 
  },
  { 
    key: 'drawing_completed', 
    label: 'Drawing', 
    type: 'boolean', 
    group: 'production_line', 
    width: '80px', 
    align: 'center', 
    condition: { 
      alwaysVisible: true,
      styles: {
        backgroundColor: (_v: any, order: any) => {
          // GREEN: If drawing checkbox is checked OR drawing is completed
          const isDrawingDone = order.metadata?.drawing_workflow?.done ?? false;
          const isDrawingCompleted = order.metadata?.drawing_work_status?.completed ?? false;
          if (isDrawingDone || isDrawingCompleted) return 'bg-green-200';
          
          // Check if order has any drawing-related features
          const hasLeadedPattern = order.decorative_leaded_pattern && 
            order.decorative_leaded_pattern.trim() !== '' && 
            order.decorative_leaded_pattern !== '-';
          const hasDuplexBar = order.duplex_bar_type && 
            order.duplex_bar_type.trim() !== '';
          const hasGeorgianBar = order.georgian_bar_type && 
            order.georgian_bar_type.trim() !== '';
          const hasQuoteDrawingImage = order.images?.some(
            (img: any) => img.feature === 'quote_drawing' && img.is_active
          ) ?? false;
          
          const needsDrawing = hasLeadedPattern || hasDuplexBar || hasGeorgianBar || hasQuoteDrawingImage;
          
          // GRAY: If order doesn't need drawing (none of the 4 features)
          if (!needsDrawing) return 'bg-gray-300';
          
          // WHITE: Needs drawing but not done yet
          return 'bg-white';
        }
      }
    }, 
    render: (value: any) => '' 
  },
  
  // Send Back buttons (only on Dashboard tab for completed items)
  { 
    key: 'send_back_sealing', 
    label: 'Send Back Sealing', 
    type: 'component', 
    group: 'send_back', 
    width: '120px', 
    align: 'center',
    condition: { alwaysVisible: true }
  },
  { 
    key: 'send_back_glass_cutting', 
    label: 'Send Back Glass Cut', 
    type: 'component', 
    group: 'send_back', 
    width: '140px', 
    align: 'center',
    condition: { alwaysVisible: true }
  },
  
  // NEW: Dashboard-specific columns (permanent rack and temporary rack are separate columns)
  { 
    key: 'temporary_rack_dashboard', 
    label: 'Temp Rack', 
    type: 'component', 
    group: 'production_line', 
    width: '120px', 
    align: 'center',
    condition: { alwaysVisible: true }
  },
  { 
    key: 'rack_dashboard', 
    label: 'RACK', 
    type: 'component', 
    group: 'production_line', 
    width: '120px', 
    align: 'center',
    condition: { alwaysVisible: true }
  },
  { 
    key: 'complete_button', 
    label: 'Complete', 
    type: 'component', 
    group: 'production_line', 
    width: '120px', 
    align: 'center',
    condition: { alwaysVisible: true }
  }
];

/**
 * Sealing & Allocation Tab - Workflow checkboxes and status columns
 */
const SEALING_ALLOCATION_TAB_COLUMNS: OrderTableColumn[] = [
  // Sealing workflow columns
  { 
    key: 'sealed_checkbox', 
    label: 'Sealed?', 
    type: 'component', 
    group: 'sealing_workflow', 
    width: '120px', 
    align: 'center',
    condition: { alwaysVisible: true }
  },
  {
    key: 'print_label_workflow',
    label: 'Print Label',
    type: 'component',
    group: 'sealing_workflow',
    width: '120px',
    align: 'center',
    condition: { alwaysVisible: true }
  },
  {
    key: 'rack_workflow',
    label: 'Rack',
    type: 'component',
    group: 'sealing_workflow',
    width: '120px',
    align: 'center',
    condition: { alwaysVisible: true }
  },
  
  // Status columns (same as Dashboard)
  { 
    key: 'glass_arrived_completed', 
    label: 'Glass Arrived', 
    type: 'boolean', 
    group: 'production_line', 
    width: '110px', 
    align: 'center', 
    condition: { 
      alwaysVisible: true,
      styles: {
        backgroundColor: (_v: any, order: any) => {
          const isScratched = order.metadata?.glass_arrival_workflow?.scratched ?? false;
          const isArrived = order.metadata?.glass_arrival_workflow?.arrived ?? false;
          
          const glassValues = [
            { hasValue: !!order.metadata?.glass_side_one_value_id, isArrival: order.glass_side_one_is_arrival },
            { hasValue: !!order.metadata?.glass_side_two_value_id, isArrival: order.glass_side_two_is_arrival },
            { hasValue: !!(order.metadata?.middle_glass_panel_value_id || order.metadata?.glass_side_three_value_id || order.metadata?.woo_glass_raw?.side_three), isArrival: order.middle_glass_panel_is_arrival }
          ];
          
          const sidesWithValues = glassValues.filter(g => g.hasValue);
          const allAreCutting = sidesWithValues.length > 0 && sidesWithValues.every(g => g.isArrival === false);
          
          const hasNoProcessingFeatures = 
            !order.hole_size_mm && 
            !order.cutout_shape && 
            !order.rounded_corner_radius_mm;
          
          const shouldGrayOut = allAreCutting && hasNoProcessingFeatures;
          
          if (isScratched) return 'bg-red-200';
          if (isArrived) return 'bg-green-200';
          if (shouldGrayOut) return 'bg-gray-300';
          return 'bg-white';
        }
      }
    }, 
    render: (value: any, order: any) => ''
  },
  { 
    key: 'glass_cutting_completed', 
    label: 'Glass Cut', 
    type: 'boolean', 
    group: 'production_line', 
    width: '90px', 
    align: 'center', 
    condition: { 
      alwaysVisible: true,
      styles: {
        backgroundColor: (_v: any, order: any) => {
          const isCutChecked = order.metadata?.glass_cutting_workflow?.cut_completed ?? false;
          if (isCutChecked) return 'bg-green-200';
          
          const glassValues = [
            { hasValue: !!order.metadata?.glass_side_one_value_id, isArrival: order.glass_side_one_is_arrival },
            { hasValue: !!order.metadata?.glass_side_two_value_id, isArrival: order.glass_side_two_is_arrival },
            { hasValue: !!(order.metadata?.middle_glass_panel_value_id || order.metadata?.glass_side_three_value_id || order.metadata?.woo_glass_raw?.side_three), isArrival: order.middle_glass_panel_is_arrival }
          ];
          
          const sidesWithValues = glassValues.filter(g => g.hasValue);
          const hasAnyCutting = sidesWithValues.some(g => g.isArrival === false);
          
          const hasProcessingFeatures = 
            !!order.hole_size_mm || 
            !!order.cutout_shape || 
            !!order.rounded_corner_radius_mm;
          
          const shouldGrayOut = !hasAnyCutting || hasProcessingFeatures;
          if (shouldGrayOut) return 'bg-gray-300';
          
          return 'bg-white';
        }
      }
    }, 
    render: (value: any) => '' 
  },
  { 
    key: 'spacer_cutting_completed', 
    label: 'Spacer Cut', 
    type: 'boolean', 
    group: 'production_line', 
    width: '90px', 
    align: 'center', 
    condition: { 
      alwaysVisible: true,
      styles: {
        backgroundColor: (_v: any, order: any) => {
          const isSpacerDone = order.metadata?.spacer_workflow?.done ?? false;
          if (isSpacerDone) return 'bg-green-200';
          
          // GRAY: If product type is single or mirror
          const isSingleOrMirror = order.product_type === 'single' || order.product_type === 'mirror';
          if (isSingleOrMirror) return 'bg-gray-300';
          
          return 'bg-white';
        }
      }
    }, 
    render: (value: any) => '' 
  },
  { 
    key: 'drawing_completed', 
    label: 'Drawing', 
    type: 'boolean', 
    group: 'production_line', 
    width: '80px', 
    align: 'center', 
    condition: { 
      alwaysVisible: true,
      styles: {
        backgroundColor: (_v: any, order: any) => {
          const isDrawingDone = order.metadata?.drawing_workflow?.done ?? false;
          const isDrawingCompleted = order.metadata?.drawing_work_status?.completed ?? false;
          if (isDrawingDone || isDrawingCompleted) return 'bg-green-200';
          
          // Check if order has any drawing-related features
          const hasLeadedPattern = order.decorative_leaded_pattern && 
            order.decorative_leaded_pattern.trim() !== '' && 
            order.decorative_leaded_pattern !== '-';
          const hasDuplexBar = order.duplex_bar_type && 
            order.duplex_bar_type.trim() !== '';
          const hasGeorgianBar = order.georgian_bar_type && 
            order.georgian_bar_type.trim() !== '';
          const hasQuoteDrawingImage = order.images?.some(
            (img: any) => img.feature === 'quote_drawing' && img.is_active
          ) ?? false;
          
          const needsDrawing = hasLeadedPattern || hasDuplexBar || hasGeorgianBar || hasQuoteDrawingImage;
          
          // GRAY: If order doesn't need drawing (none of the 4 features)
          if (!needsDrawing) return 'bg-gray-300';
          
          // WHITE: Needs drawing but not done yet
          return 'bg-white';
        }
      }
    }, 
    render: (value: any) => '' 
  },
  
  // Potential Delivery Date (read-only display)
  { 
    key: 'potential_delivery_date_readonly', 
    label: 'Potential Delivery Date', 
    type: 'date', 
    group: 'production_dates', 
    width: '160px', 
    align: 'center',
    condition: { alwaysVisible: true }
  }
];

/**
 * Glass Cutting Tab - Workflow checkboxes
 */
const GLASS_CUTTING_TAB_COLUMNS: OrderTableColumn[] = [
  { 
    key: 'glass_cut_checkbox', 
    label: 'Glass Cut?', 
    type: 'component', 
    group: 'glass_cutting_workflow', 
    width: '120px', 
    align: 'center',
    condition: { alwaysVisible: true }
  },
  {
    key: 'print_label_glass_cutting',
    label: 'Print Label',
    type: 'component',
    group: 'glass_cutting_workflow',
    width: '120px',
    align: 'center',
    condition: { alwaysVisible: true }
  },
  {
    key: 'temporary_rack_glass_cutting',
    label: 'Temporary Rack',
    type: 'component',
    group: 'glass_cutting_workflow',
    width: '120px',
    align: 'center',
    condition: { alwaysVisible: true }
  },
  {
    key: 'rack_glass_cutting',
    label: 'RACK',
    type: 'component',
    group: 'glass_cutting_workflow',
    width: '120px',
    align: 'center',
    condition: { alwaysVisible: true }
  },
  
  // Potential Delivery Date (read-only display)
  { 
    key: 'potential_delivery_date_readonly', 
    label: 'Potential Delivery Date', 
    type: 'date', 
    group: 'production_dates', 
    width: '160px', 
    align: 'center',
    condition: { alwaysVisible: true }
  }
];

/**
 * Spacer Bar Cutting Tab - Workflow checkboxes
 */
const SPACER_CUTTING_TAB_COLUMNS: OrderTableColumn[] = [
  { 
    key: 'spacer_cut_checkbox', 
    label: 'Spacer Cut', 
    type: 'component', 
    group: 'spacer_workflow', 
    width: '120px', 
    align: 'center',
    condition: { alwaysVisible: true }
  },
  {
    key: 'print_label_spacer',
    label: 'Print Label',
    type: 'component',
    group: 'spacer_workflow',
    width: '120px',
    align: 'center',
    condition: { alwaysVisible: true }
  },
  // No Rack Assignment per SPACER_BAR_CUTTING_CONDITIONS.md
];

/**
 * Drawing Tab - Workflow checkboxes and status boxes
 */
const DRAWING_TAB_COLUMNS: OrderTableColumn[] = [
  // Status boxes
  { 
    key: 'glass_arrived_completed', 
    label: 'Glass Arrived', 
    type: 'boolean', 
    group: 'production_line', 
    width: '110px', 
    align: 'center', 
    condition: { 
      alwaysVisible: true,
      styles: {
        backgroundColor: (_v: any, order: any) => {
          const isScratched = order.metadata?.glass_arrival_workflow?.scratched ?? false;
          const isArrived = order.metadata?.glass_arrival_workflow?.arrived ?? false;
          
          const glassValues = [
            { hasValue: !!order.metadata?.glass_side_one_value_id, isArrival: order.glass_side_one_is_arrival },
            { hasValue: !!order.metadata?.glass_side_two_value_id, isArrival: order.glass_side_two_is_arrival },
            { hasValue: !!(order.metadata?.middle_glass_panel_value_id || order.metadata?.glass_side_three_value_id || order.metadata?.woo_glass_raw?.side_three), isArrival: order.middle_glass_panel_is_arrival }
          ];
          
          const sidesWithValues = glassValues.filter(g => g.hasValue);
          const allAreCutting = sidesWithValues.length > 0 && sidesWithValues.every(g => g.isArrival === false);
          
          const hasNoProcessingFeatures = 
            !order.hole_size_mm && 
            !order.cutout_shape && 
            !order.rounded_corner_radius_mm;
          
          const shouldGrayOut = allAreCutting && hasNoProcessingFeatures;
          
          if (isScratched) return 'bg-red-200';
          if (isArrived) return 'bg-green-200';
          if (shouldGrayOut) return 'bg-gray-300';
          return 'bg-white';
        }
      }
    }, 
    render: (value: any, order: any) => ''
  },
  { 
    key: 'glass_cutting_completed', 
    label: 'Glass Cut', 
    type: 'boolean', 
    group: 'production_line', 
    width: '90px', 
    align: 'center', 
    condition: { 
      alwaysVisible: true,
      styles: {
        backgroundColor: (_v: any, order: any) => {
          const isCutChecked = order.metadata?.glass_cutting_workflow?.cut_completed ?? false;
          if (isCutChecked) return 'bg-green-200';
          
          const glassValues = [
            { hasValue: !!order.metadata?.glass_side_one_value_id, isArrival: order.glass_side_one_is_arrival },
            { hasValue: !!order.metadata?.glass_side_two_value_id, isArrival: order.glass_side_two_is_arrival },
            { hasValue: !!(order.metadata?.middle_glass_panel_value_id || order.metadata?.glass_side_three_value_id || order.metadata?.woo_glass_raw?.side_three), isArrival: order.middle_glass_panel_is_arrival }
          ];
          
          const sidesWithValues = glassValues.filter(g => g.hasValue);
          const hasAnyCutting = sidesWithValues.some(g => g.isArrival === false);
          
          const hasProcessingFeatures = 
            !!order.hole_size_mm || 
            !!order.cutout_shape || 
            !!order.rounded_corner_radius_mm;
          
          const shouldGrayOut = !hasAnyCutting || hasProcessingFeatures;
          if (shouldGrayOut) return 'bg-gray-300';
          
          return 'bg-white';
        }
      }
    }, 
    render: (value: any) => '' 
  },
  { 
    key: 'spacer_cutting_completed', 
    label: 'Spacer Cut', 
    type: 'boolean', 
    group: 'production_line', 
    width: '90px', 
    align: 'center', 
    condition: { 
      alwaysVisible: true,
      styles: {
        backgroundColor: (_v: any, order: any) => {
          const isSpacerDone = order.metadata?.spacer_workflow?.done ?? false;
          if (isSpacerDone) return 'bg-green-200';
          return 'bg-white';
        }
      }
    }, 
    render: (value: any) => '' 
  },
  
  // Workflow columns
  { 
    key: 'drawing_done_checkbox', 
    label: 'Drawing Done?', 
    type: 'component', 
    group: 'drawing_workflow', 
    width: '120px', 
    align: 'center',
    condition: { alwaysVisible: true }
  },
  {
    key: 'print_label_drawing',
    label: 'Print Label',
    type: 'component',
    group: 'drawing_workflow',
    width: '120px',
    align: 'center',
    condition: { alwaysVisible: true }
  },
  {
    key: 'temporary_rack_drawing',
    label: 'Temp Rack',
    type: 'component',
    group: 'drawing_workflow',
    width: '120px',
    align: 'center',
    condition: { alwaysVisible: true }
  },
  {
    key: 'rack_drawing',
    label: 'Rack',
    type: 'component',
    group: 'drawing_workflow',
    width: '120px',
    align: 'center',
    condition: { alwaysVisible: true }
  },
  
  // D1, D2, D3 aggregated columns
  {
    key: 'd1_drawing_fields',
    label: 'D1',
    type: 'component',
    group: 'drawing_details',
    width: '150px',
    condition: { alwaysVisible: true }
  },
  {
    key: 'd2_drawing_fields',
    label: 'D2',
    type: 'component',
    group: 'drawing_details',
    width: '150px',
    condition: { alwaysVisible: true }
  },
  {
    key: 'd3_drawing_fields',
    label: 'D3',
    type: 'component',
    group: 'drawing_details',
    width: '120px',
    condition: { alwaysVisible: true }
  }
];

/**
 * Tab-specific configuration interface
 */
export interface TabColumnConfig {
  addColumns: OrderTableColumn[];
  hideColumns?: string[];
  reorderColumns?: string[];
  columnOverrides?: Record<string, Partial<OrderTableColumn>>;
}

/**
 * Map of tab-specific column configurations
 * Defines what columns to add, hide, or override for each tab
 */
export const TAB_COLUMN_CONFIGS: Record<ProductionLineStage, TabColumnConfig> = {
  dashboard: {
    addColumns: DASHBOARD_TAB_COLUMNS,
    hideColumns: [
      'customer_name',
      'created_at'
    ],
    reorderColumns: [
      // 1-2: Sticky columns (must stay together!)
      'website_job_number',
      'delivery_postcode',
      
      // 3-5: Basic Info
      'product_type',
      'reference',
      'notes',
      
      // 6-8: Dates (existing tab-specific columns)
      'glass_arrival_date',
      'manufacture_date_editable',
      'potential_delivery_date',
      
      // 9-13: Status Boxes (existing tab-specific columns)
      'glass_arrived_completed',
      'glass_cutting_completed',
      'sealing_completed',
      'spacer_cutting_completed',
      'drawing_completed',
      
      // 14-15: Buttons (existing tab-specific columns)
      'send_back_sealing',
      'send_back_glass_cutting',
      
      // 16-18: Dashboard rack columns + Complete
      'temporary_rack_dashboard',
      'rack_dashboard',
      'complete_button',
      
      // 18-23: Dimensions & Glass
      'quantity',
      'glass_width',
      'glass_height',
      'diameter_mm',
      'glass_side_one_display',
      'glass_side_two_display',
      'middle_glass_panel_display',
      
      // 24-27: Options
      'polished_edges',
      'dubbed_corners',
      'aluminium_backing_foil',
      'safety_backing',
      
      // 28-30: Images
      'images_available',
      'image_types',
      'image_actions',
      
      // 31-37: Spacer Details
      'spacer_bar_type',
      'spacer_bar_color',
      'spacer_bar_thickness_mm',
      'argon_filled',
      'spacer_bar2_type',
      'spacer_bar2_color',
      'spacer_bar2_thickness_mm',
      
      // 38-47: Cutouts & Holes
      'cutout_shape',
      'cutout_count',
      'cutout_diameter_mm',
      'cutout_position',
      'hole_size_mm',
      'hole_count',
      'hole_locations',
      'rounded_corner_radius_mm',
      'rounded_corner_count',
      'rounded_corner_locations',
      
      // 48-55: Bars & Leads
      'georgian_bar_type',
      'georgian_bar_thickness',
      'duplex_bar_type',
      'duplex_bar_thickness',
      'decorative_leaded_pattern',
      'decorative_lead_color',
      'decorative_lead_thickness_mm',
      'decorative_lead_soldering_required'
    ],
    columnOverrides: {}
  },
  
  sealing_allocation: {
    addColumns: SEALING_ALLOCATION_TAB_COLUMNS,
    hideColumns: [
      'customer_name',
      'created_at',
      'polished_edges',
      'dubbed_corners',
      'hole_size_mm',
      'hole_count',
      'hole_locations',
      'cutout_shape',
      'cutout_count',
      'cutout_diameter_mm',
      'cutout_width_mm',
      'cutout_height_mm',
      'cutout_position',
      'rounded_corner_radius_mm',
      'rounded_corner_count',
      'rounded_corner_locations',
      'decorative_leaded_pattern',
      'decorative_lead_color',
      'decorative_lead_thickness_mm',
      'decorative_lead_soldering_required',
      'duplex_bar_type',
      'duplex_bar_thickness',
      'georgian_bar_type',
      'georgian_bar_thickness',
      'aluminium_backing_foil',
      'safety_backing'
    ],
    reorderColumns: [
      // 1-2: Sticky columns (must stay together!)
      'website_job_number',
      'delivery_postcode',
      
      // 3-10: Basic Info & Glass
      'product_type',
      'reference',
      'quantity',
      'glass_width',
      'glass_height',
      'diameter_mm',
      'glass_side_one_display',
      'glass_side_two_display',
      'middle_glass_panel_display',
      
      // 11-17: Spacer Details
      'spacer_bar_thickness_mm',
      'spacer_bar_type',
      'spacer_bar_color',
      'spacer_bar2_thickness_mm',
      'spacer_bar2_type',
      'spacer_bar2_color',
      'notes',
      
      // 18-21: Options & Images
      'argon_filled',
      'images_available',
      'image_types',
      'image_actions',
      
      // 22-26: Status Boxes
      'glass_arrived_completed',
      'glass_cutting_completed',
      'sealing_completed',
      'spacer_cutting_completed',
      'drawing_completed',
      
      // 27: Date
      'potential_delivery_date_readonly',
      
      // 28-30: Workflow Actions (tab-specific)
      'sealed_checkbox',
      'print_label_workflow',
      'rack_workflow'
    ],
    columnOverrides: {}
  },
  
  glass_cutting: {
    addColumns: GLASS_CUTTING_TAB_COLUMNS,
    hideColumns: [
      // Cutouts - not relevant for glass cutting workflow
      'cutout_shape',
      'cutout_count',
      'cutout_diameter_mm',
      'cutout_position',
      // Double glazed spacer details - not relevant for glass cutting
      'spacer_bar_type',
      'spacer_bar_color',
      'spacer_bar_thickness_mm',
      'argon_filled',
      // Decorative leads - not relevant for glass cutting
      'decorative_leaded_pattern',
      'decorative_lead_color',
      'decorative_lead_thickness_mm',
      'decorative_lead_soldering_required',
      // Triple glazed spacer details - not relevant for glass cutting
      'spacer_bar2_type',
      'spacer_bar2_color',
      'spacer_bar2_thickness_mm',
      // Basic columns to hide
      'customer_name',
      'created_at',
      // Hole details - not relevant for glass cutting
      'hole_size_mm',
      'hole_count',
      'hole_locations',
      // Rounded corners - not relevant for glass cutting
      'rounded_corner_radius_mm',
      'rounded_corner_count',
      'rounded_corner_locations',
      // Duplex and Georgian bars - not relevant for glass cutting
      'duplex_bar_type',
      'duplex_bar_thickness',
      'georgian_bar_type',
      'georgian_bar_thickness'
    ],
    reorderColumns: [
      // 1-2: Sticky columns (must stay together!)
      'website_job_number',
      'delivery_postcode',
      
      // 3-5: Basic Info
      'product_type',
      'reference',
      'notes',
      
      // 6-11: Dimensions & Glass
      'quantity',
      'glass_width',
      'glass_height',
      'diameter_mm',
      'glass_side_one_display',
      'glass_side_two_display',
      'middle_glass_panel_display',
      
      // 12-15: Options
      'polished_edges',
      'dubbed_corners',
      'aluminium_backing_foil',
      'safety_backing',
      
      // 16-18: Images (all 3 columns together)
      'images_available',
      'image_types',
      'image_actions',
      
      // 19: Date
      'potential_delivery_date_readonly',
      
      // 20-23: Workflow Actions (tab-specific)
      'glass_cut_checkbox',
      'print_label_glass_cutting',
      'temporary_rack_glass_cutting',
      'rack_glass_cutting'
    ],
    columnOverrides: {}
  },
  
  spacer_cutting: {
    addColumns: SPACER_CUTTING_TAB_COLUMNS,
    hideColumns: [
      'customer_name',
      'created_at',
      'glass_side_one_display',
      'glass_side_two_display',
      'middle_glass_panel_display',
      'polished_edges',
      'dubbed_corners',
      'hole_size_mm',
      'hole_count',
      'hole_locations',
      'cutout_shape',
      'cutout_count',
      'cutout_diameter_mm',
      'cutout_position',
      'rounded_corner_radius_mm',
      'rounded_corner_count',
      'rounded_corner_locations',
      'decorative_leaded_pattern',
      'decorative_lead_color',
      'decorative_lead_thickness_mm',
      'decorative_lead_soldering_required',
      'duplex_bar_type',
      'duplex_bar_thickness',
      'georgian_bar_type',
      'georgian_bar_thickness',
      'aluminium_backing_foil',
      'safety_backing'
    ],
    // Reorder columns to show spacer-related fields
    reorderColumns: [
      // 1-2: Sticky columns (must stay together!)
      'website_job_number',
      'delivery_postcode',
      
      // 3-6: Basic Info
      'product_type',
      'reference',
      'notes',
      'quantity',
      
      // 7-15: Dimensions & Spacer Details
      'glass_width',  // Shows spacer_cut_width via columnOverrides
      'glass_height', // Shows spacer_cut_height via columnOverrides
      'spacer_bar_thickness_mm',
      'spacer_bar_type',
      'spacer_bar_color',
      'argon_filled',
      'spacer_bar2_thickness_mm',
      'spacer_bar2_type',
      'spacer_bar2_color',
      
      // 16-18: Images (all 3 columns together)
      'images_available',
      'image_types',
      'image_actions',
      
      // 19-20: Workflow Actions (tab-specific) - No rack per docs
      'spacer_cut_checkbox',
      'print_label_spacer'
    ],
    // Override glass_width/height rendering to show spacer dimensions
    columnOverrides: {
      glass_width: {
        render: (value: any, order: any) => order.spacer_cut_width ?? value ?? '-'
      },
      glass_height: {
        render: (value: any, order: any) => order.spacer_cut_height ?? value ?? '-'
      }
    }
  },
  
  drawing: {
    addColumns: DRAWING_TAB_COLUMNS,
    hideColumns: [
      'customer_name',
      'created_at',
      'polished_edges',
      'dubbed_corners',
      'hole_size_mm',
      'hole_count',
      'hole_locations',
      'cutout_shape',
      'cutout_count',
      'cutout_diameter_mm',
      'cutout_position',
      'rounded_corner_radius_mm',
      'rounded_corner_count',
      'rounded_corner_locations',
      'spacer_bar_type',
      'spacer_bar_color',
      'spacer_bar_thickness_mm',
      'argon_filled',
      'duplex_bar_type',
      'duplex_bar_thickness',
      'georgian_bar_type',
      'georgian_bar_thickness',
      'spacer_bar2_type',
      'spacer_bar2_color',
      'spacer_bar2_thickness_mm',
      'aluminium_backing_foil',
      'safety_backing'
    ],
    reorderColumns: [
      // 1-2: Sticky columns (must stay together!)
      'website_job_number',
      'delivery_postcode',
      
      // 3-8: Basic Info & Dimensions
      'product_type',
      'reference',
      'notes',
      'quantity',
      'glass_width',
      'glass_height',
      'diameter_mm',
      'glass_side_one_display',
      'glass_side_two_display',
      'middle_glass_panel_display',
      
      // 9-11: Status Boxes
      'glass_arrived_completed',
      'glass_cutting_completed',
      'spacer_cutting_completed',
      
      // 12-14: Decorative lead details (auto-hide when all rows empty)
      'decorative_leaded_pattern',
      'decorative_lead_color',
      'decorative_lead_thickness_mm',
      
      // 15-17: D1, D2, D3 aggregated columns
      'd1_drawing_fields',
      'd2_drawing_fields',
      'd3_drawing_fields',
      
      // 18-20: Images (after D1–D3)
      'images_available',
      'image_types',
      'image_actions',
      
      // 18: Soldering
      'decorative_lead_soldering_required',
      
      // 19-22: Workflow Actions (tab-specific)
      'drawing_done_checkbox',
      'print_label_drawing',
      'temporary_rack_drawing',
      'rack_drawing'
    ],
    columnOverrides: {
      decorative_leaded_pattern: {
        render: (value: string | null | undefined) => {
          if (!value || value === '-') return '-';
          const labels: Record<string, string> = { square: 'Square', diamond: 'Diamond' };
          return labels[value] ?? value.charAt(0).toUpperCase() + value.slice(1);
        },
      },
      decorative_lead_color: {
        render: (value: string | null | undefined) => {
          if (!value || value === '-') return '-';
          const labels: Record<string, string> = {
            silver: 'Silver Lead',
            antique: 'Antique Lead',
          };
          return labels[value] ?? value;
        },
      },
      decorative_lead_thickness_mm: {
        render: (value: number | string | null | undefined) => {
          if (value === null || value === undefined || value === '' || value === '-') return '-';
          const str = String(value);
          return str.endsWith('mm') ? str : `${str}mm`;
        },
      },
    },
  }
};
